import Location from "../../../hooks/Location";
import { useFirestoreGeneral, useFirestoreUsers } from '../../../firebase/useFirestore';
import getSubmitterName from '../../../helpers/getSubmitterName';
import PageHeader from "../../../components/layout/PageHeader.jsx";
import { useState, useEffect, useContext } from "react";
import Tooltip from "../../../components/common/Tooltip";
import useSettings from "../../../hooks/Settings";
import SuperAdmin from '../../../hooks/SuperAdmin';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { useHistory } from "react-router-dom";
import { client } from "../../../hooks/Client.js"
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import EventResponsesCounter from '../../../components/events/EventResponsesCounter.jsx';
import PersonaNameList from '../../../components/personas/PersonaNameList.jsx';
import participantsFromResults from '../../../helpers/personas/participantsFromResults.js';
import softDeleteDocs from '../../../helpers/softDeleteDocs';
import { Auth } from '../../../StateManagment/Auth';
import DeleteModal from '../../../components/common/DeleteModal';

const EventResults = () => {
  const { personasOn: persona } = useSettings();
  const [auth] = useContext(Auth);
  // ── State
  const [sessions, setSessions] = useState([]); // [{ sessionId, date, count, results: [{...}] }]
  // De sessie die op bevestiging wacht. Eén klik verwijderde vroeger meteen, en
  // de volgende prullenbak schoof dan onder de muis (testronde 2026-10-28, B2).
  const [pendingSession, setPendingSession] = useState(/** @type {{sessionId: string, date: Date|null}|null} */ (null));

  // ── Hooks
  const eventId = Location()[3];
  const superAdmin = SuperAdmin();
  const options = { year: 'numeric', month: 'numeric', day: 'numeric'};
  const history = useHistory()

  // ── Firestore
  const eventResults = useFirestoreGeneral('EventResults', 'EventID', eventId || '');
  const users = useFirestoreUsers();

  // ── Helpers
  const toDate = (ts) => {
    if (!ts) return null;
    try {
      if (typeof ts.toDate === 'function') return ts.toDate();
      if (typeof ts === 'number') return new Date(ts);
      if (typeof ts === 'string') return new Date(ts);
      if (ts.seconds) return new Date(ts.seconds * 1000);
    } catch (e) {
      console.warn('Unable to parse timestamp', ts, e);
    }
    return null;
  };

  // Group results by session
  useEffect(() => {
    if (!eventResults || !Array.isArray(eventResults) || eventResults.length === 0) {
      setSessions([]);
      return;
    }

    const map = new Map();

    eventResults.forEach((r) => {
      const sessionId = r.SessionID || 'Unknown';
      const docId = r.docid || r.id; // support either key
      const d = toDate(r.Timestamp || r.Date || r.CreatedAt || r.Created || r.timestamp);

      const enriched = { ...r, _docId: docId, _date: d };
      if (!map.has(sessionId)) map.set(sessionId, []);
      map.get(sessionId).push(enriched);
    });

    const groups = Array.from(map.entries()).map(([sessionId, arr]) => {
      const dates = arr.map(x => x._date).filter(Boolean);
      const groupDate = dates.length ? new Date(Math.min(...dates.map(d => d.getTime()))) : null; // earliest item in session
      return {
        sessionId,
        results: arr,
        date: groupDate,
        count: arr.length,
        submitterId: arr.find(x => x.User)?.User || '',
        // Deelnemers staan in Participants op de outputregels van deze sessie.
        participants: participantsFromResults(arr),
      };
    })
    // newest sessions first
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));

    setSessions(groups);
  }, [eventResults]);

  // Een hele sessie (alle resultaten met dezelfde SessionID) naar de
  // prullenbak, niet definitief weg (ADR-008). De documenten gaan als één
  // geheel: terugzetten brengt de hele sessie terug.
  // AI-CONTEXT: See docs/pitfalls.md#ui-soft-delete-promises-a-trash-it-is-not-in
  const deleteSession = async (sessionId) => {
    if (!sessionId) return;
    try {
      const target = sessions.find(s => s.sessionId === sessionId);
      if (!target) return;

      await softDeleteDocs({
        collection: 'EventResults',
        docids: target.results.map(r => r._docId).filter(Boolean),
        authId: auth?.ID,
        title: 'Registratie',
      });

      setSessions(prev => prev.filter(s => s.sessionId !== sessionId));
    } catch (err) {
      console.error('Failed to delete session', sessionId, err);
      alert('Verwijderen mislukt. Probeer het opnieuw.');
    }
  };

  const formatDate = (d) => (d ? d.toLocaleString('nl-NL', options) : '—');

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={'Activiteit resultaten'}
          description={'Op deze pagina kun je de resultaten van deze activiteit bekijken.'}
          AI={false}
        />

        <div className="table-container dashboard-container">
          <table>
            <thead>
              <tr>
                <th>DATUM</th>
                <th>INGEVULD DOOR</th>
                {persona === 'flex' && <th>DEELNEMERS</th>}
                <th>ANTWOORDEN</th>
                {superAdmin && (
                  <th>VERWIJDEREN</th>
                )}
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && (
                <tr>
                  <td>
                    Geen resultaten gevonden.
                  </td>
                </tr>
              )}

              {sessions.map((s) => (
                <tr key={s.sessionId}>
                  <td>
                    <p>{formatDate(s.date)}</p>
                  </td>
                  <td>
                    <p>{getSubmitterName(s.submitterId, users)}</p>
                  </td>
                  {persona === 'flex' && (
                    <td>
                      <PersonaNameList personaIds={s.participants} placeholder="—" />
                    </td>
                  )}
                  <td onClick={() => history.push(`/${client}/eventresultsdetail/${s.sessionId}`)}>
                    <div id='indicator-effect-icons-container'>
                       <Tooltip content='Bekijk de responses' top={'-60px'} width={'30px'}>
                          <ChatBubbleOutlineOutlinedIcon />
                        </Tooltip>
                        <p><EventResponsesCounter results={s.results} /></p>
                    </div>
                    
                  </td>
                  {superAdmin && (
                    <td>
                      <Tooltip content='Verwijder sessie' top='-60px'>
                        <DeleteOutlinedIcon
                          className='table-delete-icon'
                          onClick={() => setPendingSession({ sessionId: s.sessionId, date: s.date })}
                          style={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DeleteModal
        deleteModal={pendingSession !== null}
        setDeleteModal={(open) => { if (!open) setPendingSession(null) }}
        deleteItem={() => deleteSession(pendingSession?.sessionId)}
        deleteName={`de registratie van ${formatDate(pendingSession?.date)}`}
        softDelete
      />
    </div>
  );
};

export default EventResults;